import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { CommandType } from '../types';
import { UseDataProps } from './types';
import { ItemData, DeclarationContext, FieldDesc } from '90.quickConnect.Models/models';
import { FORMS } from '10.quickConnect.app/routes/paths';
import { useQCSFormEvent } from '10.quickConnect.app/components/domain/Declaration/hooks/useFormEventQCScript/context';
import { useStore } from '30.quickConnect.Stores';
import { useNavigatorOnLine } from '80.quickConnect.Core/hooks';
import { FEATURE_013 } from '30.quickConnect.Stores/RootStore/LoginStore/const';

const useData = (
  handleItemDatas: (commandType: CommandType) => Promise<[boolean, string | undefined, ItemData]>,
  sendDeclaration: (newItemData: ItemData) => Promise<boolean>,
  setDeclarationData: Dispatch<SetStateAction<ItemData>>,
  context: DeclarationContext,
  flattenFields: FieldDesc[],
  declarationId: string,
): UseDataProps => {
  const { t } = useTranslation('declaration');
  const qcsFormEvent = useQCSFormEvent();
  const {
    DeclarationStore: {
      isMaxAttachment,
      requiredField,
      scrollTarget,
      sendDeclarationToAPIAsync,
      removeBackUpDcl,
      resetStore: dclResetStore,
    },
    QCScriptStore: { resetStore: qcsResetStore, setFlattenFields },
    ConsentStore: { resetStore: consentResetStore },
  } = useStore();
  const navigate = useNavigate();
  const onLine = useNavigatorOnLine();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onPublish = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const { qcScriptObj } = context;
      const [goToNextStep, nextStepIndex, newItemData] = await handleItemDatas(CommandType.Send);

      let isValid = true;

      // Vérification qcScript
      if (qcScriptObj && qcScriptObj !== '' && qcsFormEvent) {
        const { onFormValidating, displayInvalidErrorMessage } = qcsFormEvent;
        setFlattenFields(flattenFields);
        const result = onFormValidating(flattenFields);

        if (typeof result === 'boolean') isValid = result;
        if (!isValid) {
          displayInvalidErrorMessage();
          return;
        }
      }

      if (!isMaxAttachment) {
        const declarationSend = await sendDeclaration(newItemData);
        if (declarationSend) {
          if (!onLine) {
            toast.warn(t('qcapp_offline_notice'));
          }
          if (goToNextStep && nextStepIndex && newItemData && newItemData.Workflow) {
            newItemData.Workflow.currentStep = nextStepIndex;
            if (onLine) {
              sendDeclarationToAPIAsync(t);
            }
            setDeclarationData(newItemData);
          } else {
            if (newItemData.Workflow) {
              removeBackUpDcl(declarationId);
              dclResetStore();
              qcsResetStore();
              consentResetStore();
            }
            navigate(FORMS, { replace: true });
          }
        }
      } else {
        toast.warn(`${t('attachment_fields_errors_has_max_count_for_form')} (60)`);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    context,
    handleItemDatas,
    qcsFormEvent,
    isMaxAttachment,
    setFlattenFields,
    flattenFields,
    sendDeclaration,
    t,
    onLine,
    setDeclarationData,
    sendDeclarationToAPIAsync,
    navigate,
    removeBackUpDcl,
    declarationId,
    dclResetStore,
    qcsResetStore,
    consentResetStore,
  ]);

  return {
    t,
    onPublish,
    requiredField,
    scrollTarget,
    isSubmitting,
  };
};

export default useData;
